import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, HardDrive, Camera as CameraIcon, ChevronDown, RotateCcw, Info, CheckCircle2, AlertTriangle } from 'lucide-react';
import { CAMERAS, NVR_MODELS, HDD_SIZES_TB, CAMERA_TYPES, type Brand, type Camera, type CameraType, type NVRModel } from '@/data/index';

// ─── Types ────────────────────────────────────────────────────────────────────

type Mode = 'capacity' | 'time';
type SelectionMode = 'catalog' | 'manual';
type Lang = 'pt' | 'es';

interface CameraGroup {
  id: string;
  selectionMode: SelectionMode;
  brand: Brand | '';
  cameraId: string;
  quantity: number;
  bitrate: number;
  recordingHours: number;
  motionEnabled: boolean;
  motionPercentage: number;
}

interface HddOption {
  sizePerDisk: number;
  numDisks: number;
  totalCapacity: number;
  days: number;
}

// ─── Translations ─────────────────────────────────────────────────────────────

const TRANSLATIONS = {
  pt: {
    appTitle: 'Calculadora de Discos HDD',
    modeCapacityTitle: 'Calcular Capacidade Necessária',
    modeCapacityDesc: 'Descubra quantos TB precisa para o seu sistema',
    modeTimeTitle: 'Calcular Tempo de Gravação',
    modeTimeDesc: 'Saiba quantos dias grava com o seu HDD atual',
    addCamerasTitle: 'Adicionar Câmaras',
    catalogTab: '📋 Catálogo Contera',
    manualTab: '✏️ Introduzir Manualmente',
    brand: 'Marca',
    selectBrand: 'Selecione a marca...',
    type: 'Tipo',
    allTypes: 'Todos os tipos',
    model: 'Modelo',
    selectModel: 'Selecione o modelo...',
    brandOptional: 'Marca (opcional)',
    bitratePerCamera: 'Bitrate por câmara (Mbps)',
    bitrateRef: 'Referência: 2MP≈3 Mbps | 4MP≈4–6 Mbps | 8MP≈8–12 Mbps',
    numCameras: 'Nº câmaras',
    hoursPerDay: 'Horas/dia gravação',
    hPerDay: (h: number) => `${h}h/dia${h === 24 ? ' (24/7)' : ''}`,
    bitrate: 'Bitrate (Mbps)',
    motionOnly: 'Só por movimento',
    otherBrand: 'Outra / Não especificada',
    addButton: 'Carregar',
    errSelectModel: 'Selecione um modelo de câmara.',
    errBitrate: 'O bitrate deve ser maior que 0 Mbps.',
    errQty: 'A quantidade deve ser pelo menos 1.',
    cameraGroups: 'Grupos de Câmaras',
    clearAll: 'Limpar tudo',
    totalCameras: 'Total câmaras',
    cameras: 'câmaras',
    manual: 'Manual',
    paramsTitle: 'Parâmetros de Cálculo',
    retentionLabel: 'Retenção de vídeo (dias de gravação)',
    days: 'dias',
    hddCapLabel: 'Capacidade do HDD disponível (TB)',
    nvrTitle: 'Verifique se o seu NVR é compatível',
    nvrDesc: 'Selecione a marca e o modelo do NVR para verificar se é compatível com as câmaras e o armazenamento necessário.',
    nvrBrandLabel: 'Marca NVR',
    allBrands: 'Todas as marcas',
    nvrModelLabel: 'Modelo NVR',
    nvrChannels: 'Canais',
    nvrSlots: 'Slots HDD',
    nvrMaxStorage: 'Armazen. máx.',
    addCamerasPrompt: 'Adicione câmaras para calcular',
    addCamerasHint: 'Configure as câmaras no painel à esquerda para obter os resultados do cálculo de armazenamento.',
    storageNeeded: 'Armazenamento necessário',
    gbFor: (gb: string, days: number, cams: number) => `${gb} GB · para ${days} dias · ${cams} câmaras`,
    timeEstimate: 'Tempo de gravação estimado',
    insufficient: 'Insuficiente — mínimo recomendado são 30 dias de gravação.',
    withHDD: (tb: number, cams: number, gb: string) => `Com ${tb} TB · ${cams} câmaras · ${gb} GB/dia`,
    gbDay: 'GB/dia',
    gbWeek: 'GB/semana',
    gbMonth: 'GB/mês',
    hddOptions: 'Opções de HDD recomendadas',
    qty: 'Qtd.',
    total: 'Total',
    daysCol: 'Dias',
    hddNote: 'Os valores são estimativas. Os HDDs de vigilância (ex: WD Purple, Seagate SkyHawk) são recomendados para gravação contínua.',
    nvrCompatible: 'NVR Compatível',
    nvrVerify: 'Verificar Compatibilidade',
    camerasChannels: 'Câmaras / Canais disponíveis',
    storage30Days: 'Armazenamento p/ 30 dias / máximo NVR',
    hddSelected: 'HDD selecionado / mínimo p/ 30 dias',
    slotsAvail: 'Slots HDD disponíveis',
    slotMax: 'máx',
    summary: 'Resumo do sistema',
    totalCamerasLabel: 'Total de câmaras',
    dailyConsumption: 'Consumo total por dia',
    retentionTarget: 'Retenção pretendida',
    storageReq: 'Armazenamento necessário',
    hddAvail: 'HDD disponível',
    footer: '© 2026 Contera — Calculadora de Discos HDD para Videovigilância',
    bitrateTyp: 'Bitrate típico',
    bitrateMax: 'Bitrate máx',
    typeLabel: 'Tipo',
    movPct: (p: number) => `Mov. ${p}%`,
  },
  es: {
    appTitle: 'Calculadora de Discos HDD',
    modeCapacityTitle: 'Calcular Capacidad Necesaria',
    modeCapacityDesc: 'Descubra cuántos TB necesita para su sistema',
    modeTimeTitle: 'Calcular Tiempo de Grabación',
    modeTimeDesc: 'Sepa cuántos días graba con su HDD actual',
    addCamerasTitle: 'Añadir Cámaras',
    catalogTab: '📋 Catálogo Contera',
    manualTab: '✏️ Introducir Manualmente',
    brand: 'Marca',
    selectBrand: 'Seleccione la marca...',
    type: 'Tipo',
    allTypes: 'Todos los tipos',
    model: 'Modelo',
    selectModel: 'Seleccione el modelo...',
    brandOptional: 'Marca (opcional)',
    bitratePerCamera: 'Bitrate por cámara (Mbps)',
    bitrateRef: 'Referencia: 2MP≈3 Mbps | 4MP≈4–6 Mbps | 8MP≈8–12 Mbps',
    numCameras: 'Nº cámaras',
    hoursPerDay: 'Horas/día grabación',
    hPerDay: (h: number) => `${h}h/día${h === 24 ? ' (24/7)' : ''}`,
    bitrate: 'Bitrate (Mbps)',
    motionOnly: 'Sólo por movimiento',
    otherBrand: 'Otra / No especificada',
    addButton: 'Cargar',
    errSelectModel: 'Seleccione un modelo de cámara.',
    errBitrate: 'El bitrate debe ser mayor que 0 Mbps.',
    errQty: 'La cantidad debe ser al menos 1.',
    cameraGroups: 'Grupos de Cámaras',
    clearAll: 'Limpiar todo',
    totalCameras: 'Total cámaras',
    cameras: 'cámaras',
    manual: 'Manual',
    paramsTitle: 'Parámetros de Cálculo',
    retentionLabel: 'Retención de vídeo (días de grabación)',
    days: 'días',
    hddCapLabel: 'Capacidad del HDD disponible (TB)',
    nvrTitle: 'Verifique si su NVR es compatible',
    nvrDesc: 'Seleccione la marca y el modelo del NVR para verificar si es compatible con las cámaras y el almacenamiento necesario.',
    nvrBrandLabel: 'Marca NVR',
    allBrands: 'Todas las marcas',
    nvrModelLabel: 'Modelo NVR',
    nvrChannels: 'Canales',
    nvrSlots: 'Slots HDD',
    nvrMaxStorage: 'Almacen. máx.',
    addCamerasPrompt: 'Añada cámaras para calcular',
    addCamerasHint: 'Configure las cámaras en el panel izquierdo para obtener los resultados del cálculo de almacenamiento.',
    storageNeeded: 'Almacenamiento necesario',
    gbFor: (gb: string, days: number, cams: number) => `${gb} GB · para ${days} días · ${cams} cámaras`,
    timeEstimate: 'Tiempo de grabación estimado',
    insufficient: 'Insuficiente — el mínimo recomendado son 30 días de grabación.',
    withHDD: (tb: number, cams: number, gb: string) => `Con ${tb} TB · ${cams} cámaras · ${gb} GB/día`,
    gbDay: 'GB/día',
    gbWeek: 'GB/semana',
    gbMonth: 'GB/mes',
    hddOptions: 'Opciones de HDD recomendadas',
    qty: 'Cant.',
    total: 'Total',
    daysCol: 'Días',
    hddNote: 'Los valores son estimaciones. Los HDDs de vigilancia (ej: WD Purple, Seagate SkyHawk) son recomendados para grabación continua.',
    nvrCompatible: 'NVR Compatible',
    nvrVerify: 'Verificar Compatibilidad',
    camerasChannels: 'Cámaras / Canales disponibles',
    storage30Days: 'Almacenamiento p/ 30 días / máximo NVR',
    hddSelected: 'HDD seleccionado / mínimo p/ 30 días',
    slotsAvail: 'Slots HDD disponibles',
    slotMax: 'máx',
    summary: 'Resumen del sistema',
    totalCamerasLabel: 'Total de cámaras',
    dailyConsumption: 'Consumo total por día',
    retentionTarget: 'Retención pretendida',
    storageReq: 'Almacenamiento necesario',
    hddAvail: 'HDD disponible',
    footer: '© 2026 Contera — Calculadora de Discos HDD para Videovigilancia',
    bitrateTyp: 'Bitrate típico',
    bitrateMax: 'Bitrate máx',
    typeLabel: 'Tipo',
    movPct: (p: number) => `Mov. ${p}%`,
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

function calcStorageGB(groups: CameraGroup[], retentionDays: number): number {
  return groups.reduce((total, g) => {
    if (g.quantity <= 0 || g.bitrate <= 0 || g.recordingHours <= 0) return total;
    const effectiveBitrate = g.motionEnabled ? g.bitrate * (g.motionPercentage / 100) : g.bitrate;
    const gb = (effectiveBitrate * 3600 * g.recordingHours * retentionDays * g.quantity) / (8 * 1000);
    return total + gb;
  }, 0);
}

function calcDailyGB(groups: CameraGroup[]): number {
  return groups.reduce((total, g) => {
    if (g.quantity <= 0 || g.bitrate <= 0 || g.recordingHours <= 0) return total;
    const effectiveBitrate = g.motionEnabled ? g.bitrate * (g.motionPercentage / 100) : g.bitrate;
    return total + (effectiveBitrate * 3600 * g.recordingHours * g.quantity) / (8 * 1000);
  }, 0);
}

function calcDaysFromHDD(groups: CameraGroup[], hddTB: number): number {
  const dailyGB = calcDailyGB(groups);
  if (dailyGB <= 0) return 0;
  return (hddTB * 1000) / dailyGB;
}

function buildHddOptions(requiredGB: number): HddOption[] {
  return HDD_SIZES_TB.map((size) => {
    const numDisks = Math.ceil(requiredGB / (size * 1000));
    const totalCapacity = numDisks * size;
    const days = (totalCapacity * 1000) / (requiredGB / 1 || 1);
    return { sizePerDisk: size, numDisks, totalCapacity, days };
  }).filter((o) => o.numDisks <= 8);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const BRAND_COLORS: Record<Brand, string> = {
  DAHUA: 'bg-red-500/15 text-red-400 border-red-500/30',
  TVT: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  AJAX: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
};

const BRAND_DOT: Record<Brand, string> = {
  DAHUA: 'bg-red-500',
  TVT: 'bg-blue-500',
  AJAX: 'bg-orange-500',
};

function BrandBadge({ brand }: { brand: Brand }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${BRAND_COLORS[brand]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${BRAND_DOT[brand]}`} />
      {brand}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Calculator() {
  const [lang, setLang] = useState<Lang>('pt');
  const T = TRANSLATIONS[lang];

  const [mode, setMode] = useState<Mode>('capacity');
  const [groups, setGroups] = useState<CameraGroup[]>([]);
  const [retentionDays, setRetentionDays] = useState(30);
  const [hddCapacityTB, setHddCapacityTB] = useState(4);
  const [selectedNVRId, setSelectedNVRId] = useState('');
  const [nvrBrand, setNvrBrand] = useState<Brand | ''>('');
  const [showNVRPanel, setShowNVRPanel] = useState(false);

  const [formSelMode, setFormSelMode] = useState<SelectionMode>('catalog');
  const [formBrand, setFormBrand] = useState<Brand | ''>('');
  const [formCameraType, setFormCameraType] = useState<CameraType | ''>('');
  const [formCameraId, setFormCameraId] = useState('');
  const [formQty, setFormQty] = useState(1);
  const [formBitrate, setFormBitrate] = useState(4);
  const [formHours, setFormHours] = useState(24);
  const [formMotion, setFormMotion] = useState(false);
  const [formMotionPct, setFormMotionPct] = useState(25);
  const [formError, setFormError] = useState('');

  const typesForBrand = useMemo(
    () => [...new Set(CAMERAS.filter((c) => c.brand === formBrand).map((c) => c.type))],
    [formBrand]
  );

  const camerasForBrand = useMemo(
    () => CAMERAS.filter((c) => c.brand === formBrand && (!formCameraType || c.type === formCameraType)),
    [formBrand, formCameraType]
  );

  const selectedCamera = useMemo(() => CAMERAS.find((c) => c.id === formCameraId), [formCameraId]);
  const selectedNVR = useMemo(() => NVR_MODELS.find((n) => n.id === selectedNVRId), [selectedNVRId]);
  const nvrsForBrand = useMemo(
    () => (nvrBrand ? NVR_MODELS.filter((n) => n.brand === nvrBrand) : NVR_MODELS),
    [nvrBrand]
  );

  const handleCameraSelect = useCallback((id: string) => {
    setFormCameraId(id);
    const cam = CAMERAS.find((c) => c.id === id);
    if (cam) setFormBitrate(cam.bitrateTypical);
  }, []);

  const handleBrandChange = useCallback((brand: Brand | '') => {
    setFormBrand(brand);
    setFormCameraType('');
    setFormCameraId('');
    setFormBitrate(4);
  }, []);

  const handleAddGroup = () => {
    if (formSelMode === 'catalog' && !formCameraId) { setFormError(T.errSelectModel); return; }
    if (formBitrate <= 0) { setFormError(T.errBitrate); return; }
    if (formQty <= 0) { setFormError(T.errQty); return; }
    setFormError('');
    setGroups((prev) => [...prev, {
      id: uid(), selectionMode: formSelMode, brand: formBrand, cameraId: formCameraId,
      quantity: formQty, bitrate: formBitrate, recordingHours: formHours,
      motionEnabled: formMotion, motionPercentage: formMotionPct,
    }]);
    setFormCameraId(''); setFormBrand(''); setFormCameraType('');
    setFormQty(1); setFormBitrate(4); setFormHours(24);
    setFormMotion(false); setFormMotionPct(25);
  };

  const handleRemoveGroup = (id: string) => setGroups((prev) => prev.filter((g) => g.id !== id));
  const handleReset = () => { setGroups([]); setSelectedNVRId(''); setNvrBrand(''); };

  // ── Results ────────────────────────────────────────────────────
  const totalCameras = useMemo(() => groups.reduce((s, g) => s + g.quantity, 0), [groups]);
  const requiredGB = useMemo(() => calcStorageGB(groups, retentionDays), [groups, retentionDays]);
  const requiredTB = requiredGB / 1000;
  const dailyGB = useMemo(() => calcDailyGB(groups), [groups]);
  const daysFromHDD = useMemo(() => calcDaysFromHDD(groups, hddCapacityTB), [groups, hddCapacityTB]);

  const hddOptions = useMemo(() => {
    if (requiredGB <= 0) return [];
    return buildHddOptions(requiredGB).slice(0, 6);
  }, [requiredGB]);

  const requiredFor30DaysGB = useMemo(() => calcStorageGB(groups, 30), [groups]);
  const requiredFor30DaysTB = requiredFor30DaysGB / 1000;
  const nvrCompatible = selectedNVR
    ? totalCameras <= selectedNVR.channels &&
      requiredFor30DaysTB <= selectedNVR.maxTotalStorage &&
      (mode === 'time' ? daysFromHDD >= 30 : true)
    : null;

  const hasGroups = groups.length > 0;
  const hasResult = hasGroups && (mode === 'capacity' ? requiredGB > 0 : daysFromHDD > 0);

  // NVR card — calculado explicitamente fora do JSX
  const nvrChannelsOk = selectedNVR ? totalCameras <= selectedNVR.channels : false;
  const nvrStorageOk  = selectedNVR ? requiredFor30DaysTB <= selectedNVR.maxTotalStorage : false;
  const nvrHddOk      = mode !== 'time' || daysFromHDD >= 30;
  const nvrAllOk      = nvrChannelsOk && nvrStorageOk && nvrHddOk;

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── Header ── */}
      <header className="border-b border-border/60 bg-card/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-md">
              <HardDrive className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground font-medium tracking-widest uppercase">Contera</div>
              <div className="text-base font-bold leading-tight">{T.appTitle}</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {/* Language switcher */}
            <div className="flex items-center gap-0.5 bg-muted rounded-lg p-0.5">
              {(['pt', 'es'] as Lang[]).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                    lang === l ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            {(['DAHUA', 'TVT', 'AJAX'] as Brand[]).map((b) => (
              <BrandBadge key={b} brand={b} />
            ))}
          </div>
        </div>
      </header>

      {/* ── Mode Selector ── */}
      <div className="bg-muted/40 border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            {[
              { key: 'capacity' as Mode, icon: '💾', title: T.modeCapacityTitle, desc: T.modeCapacityDesc },
              { key: 'time' as Mode, icon: '⏱️', title: T.modeTimeTitle, desc: T.modeTimeDesc },
            ].map((m) => (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                className={`text-left rounded-xl border p-4 transition-all duration-200 ${
                  mode === m.key
                    ? 'border-primary bg-primary/10 shadow-sm'
                    : 'border-border bg-card hover:border-primary/40 hover:bg-card/80'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{m.icon}</span>
                  <div>
                    <div className={`text-sm font-semibold ${mode === m.key ? 'text-primary' : 'text-foreground'}`}>{m.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{m.desc}</div>
                  </div>
                  {mode === m.key && <CheckCircle2 className="w-4 h-4 text-primary ml-auto shrink-0" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* LEFT PANEL */}
          <div className="lg:col-span-3 space-y-4">

            {/* ─ Add Camera Section ─ */}
            <div className="bg-card rounded-2xl border border-border/60 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <CameraIcon className="w-4 h-4 text-primary" />
                <h2 className="font-semibold text-sm">{T.addCamerasTitle}</h2>
              </div>

              <div className="flex gap-1 bg-muted rounded-lg p-1 mb-4 w-fit">
                {(['catalog', 'manual'] as SelectionMode[]).map((sm) => (
                  <button
                    key={sm}
                    onClick={() => { setFormSelMode(sm); setFormCameraId(''); setFormBrand(''); }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                      formSelMode === sm ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {sm === 'catalog' ? T.catalogTab : T.manualTab}
                  </button>
                ))}
              </div>

              {/* Catalog mode */}
              {formSelMode === 'catalog' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{T.brand}</label>
                    <select
                      value={formBrand}
                      onChange={(e) => handleBrandChange(e.target.value as Brand | '')}
                      className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    >
                      <option value="">{T.selectBrand}</option>
                      {(['DAHUA', 'TVT', 'AJAX'] as Brand[]).map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{T.type}</label>
                    <select
                      value={formCameraType}
                      onChange={(e) => { setFormCameraType(e.target.value as CameraType | ''); setFormCameraId(''); }}
                      disabled={!formBrand}
                      className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
                    >
                      <option value="">{T.allTypes}</option>
                      {typesForBrand.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{T.model}</label>
                    <select
                      value={formCameraId}
                      onChange={(e) => handleCameraSelect(e.target.value)}
                      disabled={!formBrand}
                      className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
                    >
                      <option value="">{T.selectModel}</option>
                      {camerasForBrand.map((c) => (
                        <option key={c.id} value={c.id}>{c.model}</option>
                      ))}
                    </select>
                  </div>
                  {selectedCamera && (
                    <div className="sm:col-span-2 bg-muted/50 rounded-lg px-3 py-2 text-xs text-muted-foreground flex flex-wrap gap-4">
                      <span>📷 {selectedCamera.resolution}</span>
                      <span>📡 {T.bitrateTyp}: <strong className="text-foreground">{selectedCamera.bitrateTypical} Mbps</strong></span>
                      <span>📡 {T.bitrateMax}: <strong className="text-foreground">{selectedCamera.bitrateMax} Mbps</strong></span>
                      <span>🔲 {T.typeLabel}: {selectedCamera.type}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Manual mode */}
              {formSelMode === 'manual' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{T.brandOptional}</label>
                    <select
                      value={formBrand}
                      onChange={(e) => setFormBrand(e.target.value as Brand | '')}
                      className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    >
                      <option value="">{T.otherBrand}</option>
                      {(['DAHUA', 'TVT', 'AJAX'] as Brand[]).map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{T.bitratePerCamera}</label>
                    <input
                      type="number" min={0.1} max={100} step={0.1}
                      value={formBitrate}
                      onChange={(e) => setFormBitrate(parseFloat(e.target.value) || 0)}
                      className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    <div className="mt-1 text-xs text-muted-foreground">{T.bitrateRef}</div>
                  </div>
                </div>
              )}

              {/* Shared fields */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{T.numCameras}</label>
                  <input
                    type="number" min={1} max={256}
                    value={formQty}
                    onChange={(e) => setFormQty(parseInt(e.target.value) || 1)}
                    className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{T.hoursPerDay}</label>
                  <select
                    value={formHours}
                    onChange={(e) => setFormHours(parseInt(e.target.value))}
                    className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    {[1,2,4,6,8,10,12,16,18,20,22,24].map((h) => (
                      <option key={h} value={h}>{T.hPerDay(h)}</option>
                    ))}
                  </select>
                </div>
                {formSelMode === 'catalog' && (
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{T.bitrate}</label>
                    <input
                      type="number" min={0.1} max={100} step={0.1}
                      value={formBitrate}
                      onChange={(e) => setFormBitrate(parseFloat(e.target.value) || 0)}
                      className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                )}
                <div className="flex flex-col justify-end">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{T.motionOnly}</label>
                  <div className="flex items-center gap-2 h-9">
                    <button
                      onClick={() => setFormMotion(!formMotion)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${formMotion ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${formMotion ? 'translate-x-5' : ''}`} />
                    </button>
                    {formMotion && (
                      <select
                        value={formMotionPct}
                        onChange={(e) => setFormMotionPct(parseInt(e.target.value))}
                        className="text-xs bg-muted border border-border rounded px-1 py-0.5 focus:outline-none"
                      >
                        {[10,15,20,25,30,40,50,60,75].map((p) => (
                          <option key={p} value={p}>{p}%</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {formError && (
                <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2 mb-3">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {formError}
                </div>
              )}

              <button
                onClick={handleAddGroup}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-xl text-sm font-semibold hover:opacity-90 active:scale-95 transition-all"
              >
                <Plus className="w-4 h-4" /> {T.addButton}
              </button>
            </div>

            {/* ─ Camera Groups List ─ */}
            <AnimatePresence>
              {hasGroups && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card rounded-2xl border border-border/60 p-5 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="font-semibold text-sm flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 text-xs flex items-center justify-center font-bold">{groups.length}</span>
                      {T.cameraGroups}
                    </h2>
                    <button onClick={handleReset} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1 transition-colors">
                      <RotateCcw className="w-3 h-3" /> {T.clearAll}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {groups.map((g) => {
                      const cam = CAMERAS.find((c) => c.id === g.cameraId);
                      const effBitrate = g.motionEnabled ? g.bitrate * (g.motionPercentage / 100) : g.bitrate;
                      const groupGB = (effBitrate * 3600 * g.recordingHours * retentionDays * g.quantity) / (8 * 1000);
                      return (
                        <div key={g.id} className="flex items-center justify-between bg-muted/40 rounded-xl px-3 py-2.5 gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-lg">📷</span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-medium text-sm">{g.quantity}×</span>
                                {g.brand && <BrandBadge brand={g.brand as Brand} />}
                                <span className="text-sm text-foreground/80 truncate">{cam ? cam.model : T.manual}</span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {g.recordingHours}h/dia · {g.bitrate} Mbps{g.motionEnabled ? ` · ${T.movPct(g.motionPercentage)}` : ''} · <span className="font-mono">{(groupGB / 1000).toFixed(2)} TB</span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveGroup(g.id)}
                            className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-border/40 mt-3 pt-3 flex justify-between text-sm">
                    <span className="text-muted-foreground">{T.totalCameras}</span>
                    <span className="font-bold">{totalCameras} {T.cameras}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ─ Parameters ─ */}
            <div className="bg-card rounded-2xl border border-border/60 p-5 shadow-sm">
              <h2 className="font-semibold text-sm mb-4 flex items-center gap-2">
                <span>⚙️</span> {T.paramsTitle}
              </h2>

              {mode === 'capacity' && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-2">{T.retentionLabel}</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range" min={1} max={365} value={retentionDays}
                      onChange={(e) => setRetentionDays(parseInt(e.target.value))}
                      className="flex-1 accent-primary"
                    />
                    <div className="flex items-center gap-1">
                      <input
                        type="number" min={1} max={365} value={retentionDays}
                        onChange={(e) => setRetentionDays(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-16 bg-muted border border-border rounded-lg px-2 py-1.5 text-sm text-center font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                      <span className="text-xs text-muted-foreground">{T.days}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {[7,14,30,60,90].map((d) => (
                      <button
                        key={d}
                        onClick={() => setRetentionDays(d)}
                        className={`text-xs px-2.5 py-1 rounded-lg border transition-all ${retentionDays === d ? 'border-primary bg-primary/10 text-primary font-semibold' : 'border-border text-muted-foreground hover:border-primary/40'}`}
                      >
                        {d} {T.days}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {mode === 'time' && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-2">{T.hddCapLabel}</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range" min={1} max={20} step={1} value={hddCapacityTB}
                      onChange={(e) => setHddCapacityTB(parseInt(e.target.value))}
                      className="flex-1 accent-primary"
                    />
                    <div className="flex items-center gap-1">
                      <input
                        type="number" min={0.1} max={200} step={0.5} value={hddCapacityTB}
                        onChange={(e) => setHddCapacityTB(parseFloat(e.target.value) || 1)}
                        className="w-16 bg-muted border border-border rounded-lg px-2 py-1.5 text-sm text-center font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                      <span className="text-xs text-muted-foreground">TB</span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {[1, 2, 4, 6, 8, 10, 12, 16].map((t) => (
                      <button
                        key={t}
                        onClick={() => setHddCapacityTB(t)}
                        className={`text-xs px-2.5 py-1 rounded-lg border transition-all ${hddCapacityTB === t ? 'border-primary bg-primary/10 text-primary font-semibold' : 'border-border text-muted-foreground hover:border-primary/40'}`}
                      >
                        {t} TB
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ─ NVR Section ─ */}
            <div className="bg-card rounded-2xl border border-border/60 shadow-sm overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold hover:bg-muted/20 transition-colors"
                onClick={() => setShowNVRPanel(!showNVRPanel)}
              >
                <span className="flex items-center gap-2"><span>🖥️</span> {T.nvrTitle}</span>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showNVRPanel ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {showNVRPanel && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 border-t border-border/40 pt-4">
                      <p className="text-xs text-muted-foreground mb-3">{T.nvrDesc}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1">{T.nvrBrandLabel}</label>
                          <select
                            value={nvrBrand}
                            onChange={(e) => { setNvrBrand(e.target.value as Brand | ''); setSelectedNVRId(''); }}
                            className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                          >
                            <option value="">{T.allBrands}</option>
                            {(['DAHUA', 'TVT', 'AJAX'] as Brand[]).map((b) => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1">{T.nvrModelLabel}</label>
                          <select
                            value={selectedNVRId}
                            onChange={(e) => setSelectedNVRId(e.target.value)}
                            className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                            disabled={nvrsForBrand.length === 0}
                          >
                            <option value="">{T.selectModel}</option>
                            {nvrsForBrand.map((n) => (
                              <option key={n.id} value={n.id}>{n.model}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {selectedNVR && (
                        <div className="mt-3 bg-muted/50 rounded-lg p-3 text-xs grid grid-cols-3 gap-2">
                          <div><span className="text-muted-foreground block">{T.nvrChannels}</span><strong>{selectedNVR.channels}CH</strong></div>
                          <div><span className="text-muted-foreground block">{T.nvrSlots}</span><strong>{selectedNVR.hddSlots}</strong></div>
                          <div><span className="text-muted-foreground block">{T.nvrMaxStorage}</span><strong>{selectedNVR.maxTotalStorage} TB</strong></div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="lg:col-span-2 space-y-4">

            {!hasGroups ? (
              <div className="bg-card rounded-2xl border border-border/60 p-8 text-center shadow-sm">
                <div className="text-4xl mb-3">💾</div>
                <div className="font-semibold text-foreground mb-1">{T.addCamerasPrompt}</div>
                <div className="text-sm text-muted-foreground">{T.addCamerasHint}</div>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div key={mode} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">

                  {/* Main Result Card */}
                  {mode === 'capacity' && (
                    <div className="bg-card rounded-2xl border border-primary/30 p-5 shadow-sm">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{T.storageNeeded}</div>
                      <div className="flex items-end gap-2 mb-1">
                        <span className="text-4xl font-bold font-mono text-primary">{requiredTB.toFixed(2)}</span>
                        <span className="text-xl font-semibold text-muted-foreground mb-1">TB</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{T.gbFor(Math.round(requiredGB).toLocaleString('pt-PT'), retentionDays, totalCameras)}</div>
                      <div className="mt-3 space-y-1">
                        {groups.map((g) => {
                          const cam = CAMERAS.find((c) => c.id === g.cameraId);
                          const effBitrate = g.motionEnabled ? g.bitrate * (g.motionPercentage / 100) : g.bitrate;
                          const groupGB = (effBitrate * 3600 * g.recordingHours * retentionDays * g.quantity) / (8 * 1000);
                          const pct = requiredGB > 0 ? (groupGB / requiredGB) * 100 : 0;
                          return (
                            <div key={g.id}>
                              <div className="flex justify-between text-xs mb-0.5">
                                <span className="text-muted-foreground truncate">{g.quantity}× {cam ? cam.model : T.manual}</span>
                                <span className="font-mono ml-2 shrink-0">{(groupGB / 1000).toFixed(2)} TB</span>
                              </div>
                              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {mode === 'time' && (
                    <div className={`bg-card rounded-2xl border p-5 shadow-sm ${daysFromHDD < 30 ? 'border-orange-400/40' : 'border-primary/30'}`}>
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{T.timeEstimate}</div>
                      <div className="flex items-end gap-2 mb-1">
                        <span className={`text-4xl font-bold font-mono ${daysFromHDD < 30 ? 'text-orange-400' : 'text-primary'}`}>{Math.floor(daysFromHDD)}</span>
                        <span className="text-xl font-semibold text-muted-foreground mb-1">{T.days}</span>
                      </div>
                      {daysFromHDD < 30 && (
                        <div className="flex items-center gap-1.5 text-xs text-orange-400 font-medium mb-2">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {T.insufficient}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        {T.withHDD(hddCapacityTB, totalCameras, dailyGB.toFixed(1))}
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        {[
                          { label: T.gbDay, val: dailyGB.toFixed(1) },
                          { label: T.gbWeek, val: (dailyGB * 7).toFixed(0) },
                          { label: T.gbMonth, val: (dailyGB * 30).toFixed(0) },
                        ].map((s) => (
                          <div key={s.label} className="bg-muted/50 rounded-lg p-2 text-center">
                            <div className="font-mono text-sm font-bold">{s.val}</div>
                            <div className="text-xs text-muted-foreground">{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* HDD Options Table */}
                  {mode === 'capacity' && hddOptions.length > 0 && (
                    <div className="bg-card rounded-2xl border border-border/60 p-5 shadow-sm">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <HardDrive className="w-3.5 h-3.5" /> {T.hddOptions}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-muted-foreground border-b border-border/40">
                              <th className="text-left py-1.5 font-medium">HDD</th>
                              <th className="text-center py-1.5 font-medium">{T.qty}</th>
                              <th className="text-center py-1.5 font-medium">{T.total}</th>
                              <th className="text-right py-1.5 font-medium">{T.daysCol}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {hddOptions.map((opt, i) => {
                              const daysResult = dailyGB > 0 ? (opt.totalCapacity * 1000) / dailyGB : 0;
                              const isRecommended = i === Math.min(2, hddOptions.length - 1);
                              return (
                                <tr key={opt.sizePerDisk} className={`border-b border-border/20 last:border-0 ${isRecommended ? 'bg-primary/5' : ''}`}>
                                  <td className="py-2 font-mono font-semibold">{opt.sizePerDisk} TB{isRecommended ? ' ⭐' : ''}</td>
                                  <td className="py-2 text-center">{opt.numDisks}×</td>
                                  <td className="py-2 text-center font-mono">{opt.totalCapacity} TB</td>
                                  <td className={`py-2 text-right font-mono font-semibold ${daysResult >= retentionDays ? 'text-green-500' : 'text-orange-400'}`}>
                                    {Math.floor(daysResult)}d
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex items-start gap-1.5 mt-3 text-xs text-muted-foreground bg-muted/30 rounded-lg p-2">
                        <Info className="w-3 h-3 mt-0.5 shrink-0" />
                        <span>{T.hddNote}</span>
                      </div>
                    </div>
                  )}

                  {/* NVR Compatibility */}
                  {selectedNVR && hasGroups && mode === 'time' && !nvrHddOk && (
                    <div style={{ borderWidth:'1px', borderStyle:'solid', borderColor:'rgba(251,146,60,0.6)', backgroundColor:'rgba(251,146,60,0.08)', borderRadius:'1rem', padding:'1rem' }}>
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-orange-400" />
                        <span className="text-sm font-semibold" style={{ color:'rgba(251,146,60,1)' }}>
                          {lang === 'pt' ? 'HDD Insuficiente para o NVR' : 'HDD Insuficiente para el NVR'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {lang === 'pt'
                          ? `O HDD de ${hddCapacityTB} TB só grava ${Math.floor(daysFromHDD)} dias. Para compatibilidade NVR são necessários pelo menos ${requiredFor30DaysTB.toFixed(1)} TB (30 dias).`
                          : `El HDD de ${hddCapacityTB} TB solo graba ${Math.floor(daysFromHDD)} días. Para compatibilidad NVR se necesitan al menos ${requiredFor30DaysTB.toFixed(1)} TB (30 días).`}
                      </p>
                    </div>
                  )}
                  {selectedNVR && hasGroups && !(mode === 'time' && !nvrHddOk) && (
                    <div style={{ borderWidth:'1px', borderStyle:'solid', borderColor: nvrAllOk ? 'rgba(34,197,94,0.4)' : 'rgba(251,146,60,0.6)', backgroundColor: nvrAllOk ? 'rgba(34,197,94,0.06)' : 'rgba(251,146,60,0.08)', borderRadius:'1rem', padding:'1rem', boxShadow:'0 1px 2px 0 rgba(0,0,0,.05)' }}>
                      <div className="flex items-center gap-2 mb-2">
                        {nvrAllOk ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-orange-400" />}
                        <span className="text-sm font-semibold" style={{ color: nvrAllOk ? undefined : 'rgba(251,146,60,1)' }}>
                          {nvrAllOk ? T.nvrCompatible : T.nvrVerify}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div className="flex justify-between">
                          <span>{T.camerasChannels}</span>
                          <span style={{ color: nvrChannelsOk ? 'rgb(34,197,94)' : 'rgb(251,146,60)' }} className="font-mono font-semibold">
                            {totalCameras} / {selectedNVR.channels}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>{T.storage30Days}</span>
                          <span style={{ color: nvrStorageOk ? 'rgb(34,197,94)' : 'rgb(251,146,60)' }} className="font-mono font-semibold">
                            {requiredFor30DaysTB.toFixed(1)} / {selectedNVR.maxTotalStorage} TB
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>{T.slotsAvail}</span>
                          <span className="font-mono font-semibold">
                            {selectedNVR.hddSlots} slot{selectedNVR.hddSlots > 1 ? 's' : ''} ({T.slotMax} {selectedNVR.maxStoragePerSlot} TB)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Summary Box */}
                  <div className="bg-muted/30 rounded-2xl border border-border/40 p-4">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{T.summary}</div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{T.totalCamerasLabel}</span>
                        <span className="font-semibold">{totalCameras}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{T.dailyConsumption}</span>
                        <span className="font-mono font-semibold">{dailyGB.toFixed(1)} GB/{T.days === 'dias' ? 'dia' : 'día'}</span>
                      </div>
                      {mode === 'capacity' && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{T.retentionTarget}</span>
                          <span className="font-semibold">{retentionDays} {T.days}</span>
                        </div>
                      )}
                      {mode === 'capacity' && (
                        <div className="flex justify-between border-t border-border/40 pt-1.5 mt-1">
                          <span className="text-muted-foreground font-medium">{T.storageReq}</span>
                          <span className="font-mono font-bold text-primary">{requiredTB.toFixed(2)} TB</span>
                        </div>
                      )}
                      {mode === 'time' && (
                        <div className="flex justify-between border-t border-border/40 pt-1.5 mt-1">
                          <span className="text-muted-foreground font-medium">{T.hddAvail}</span>
                          <span className="font-mono font-bold text-primary">{hddCapacityTB} TB → {Math.floor(daysFromHDD)} {T.days}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="border-t border-border/40 mt-8 py-6 text-center text-xs text-muted-foreground">
        <p>{T.footer}</p>
      </footer>
    </div>
  );
}
